import {useMemo} from 'react';
import styled from 'styled-components';
import {Icon, Table} from '@forecast-it/design-system';
import {useNavigate} from '../../../router/compat';
import type {Ticket, TicketStatus} from '../types/ticket';
import {TicketListCard} from './TicketCard.list';
import {TicketGroupHeader} from './TicketGroupHeader';
import {PriorityFlag} from './PriorityBadge';
import {COLUMN_LABELS, COLUMN_WIDTHS, DEFAULT_VISIBLE_COLUMN_KEYS, type ColumnKey} from '../constants/listColumns';
import {STATUS_ORDER} from '../constants/statuses';
import {useGroupCollapse} from '../hooks/useGroupCollapse';
import {slaPresentation} from '../utils/slaPresentation';
import {ticketTableRows, type TicketTableRow} from '../utils/ticketTableRows';

interface Props {
	tickets: Ticket[];
	/** Opens the assign flow for a single ticket. Omitted when the viewer can't assign. */
	onAssign?: (ticketId: string) => void;
	/** Opens the delete confirmation. Omitted when the viewer can't manage tickets. */
	onDelete?: (ticketId: string) => void;
	/**
	 * Receives the selected tickets for one status group, keyed by status. The
	 * caller merges across groups — each group is its own table, so a group only
	 * ever reports its own rows.
	 */
	onSelectionChange?: (status: TicketStatus, selected: Ticket[]) => void;
	/**
	 * Bumped by the caller to drop every group's selection. The design-system table
	 * owns its selection internally, so clearing means remounting it.
	 */
	selectionEpoch?: number;
	/**
	 * Which columns are visible. When omitted, the default set is shown — matching
	 * the table's pre-toggle behaviour so existing callers need no update.
	 */
	visibleColumns?: ReadonlySet<ColumnKey>;
}

// Order is the visual order of the queue: newest work first, settled work last.
const STATUS_GROUP_ORDER = STATUS_ORDER;

function groupByStatus(tickets: Ticket[]): Map<TicketStatus, Ticket[]> {
	const groups = new Map<TicketStatus, Ticket[]>();
	for (const status of STATUS_GROUP_ORDER) {
		const matching = tickets.filter(t => t.status === status);
		if (matching.length > 0) {
			groups.set(status, matching);
		}
	}
	return groups;
}

const Groups = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[600]};
`;
const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;
// The design-system table is a desktop affordance; below md the same tickets read
// as cards. Base styles are the visible state and the media query hides, because
// jsdom (which has no layout) treats a base `display: none` as hidden.
const TableWrap = styled.div`
	display: block;
	@media (max-width: 767px) {
		display: none;
	}
`;
const MobileStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	@media (min-width: 768px) {
		display: none;
	}
`;

export function TicketListTable({tickets, onAssign, onDelete, onSelectionChange, selectionEpoch = 0, visibleColumns}: Props) {
	const navigate = useNavigate();
	const visible = visibleColumns ?? DEFAULT_VISIBLE_COLUMN_KEYS;
	const {isCollapsed, toggle} = useGroupCollapse();
	const groups = useMemo(() => groupByStatus(tickets), [tickets]);

	const actionOptions = useMemo(() => {
		const options = [];
		if (onAssign) {
			options.push(
				<Table.ActionColumn.Option
					key="assign"
					onClick={(row: TicketTableRow) => onAssign(row.rowId)}
					data-cy="ticket-assign"
				>
					Assign…
				</Table.ActionColumn.Option>
			);
		}
		if (onDelete) {
			options.push(
				<Table.ActionColumn.Option
					key="delete"
					onClick={(row: TicketTableRow) => onDelete(row.rowId)}
					data-cy="ticket-delete"
				>
					Delete ticket
				</Table.ActionColumn.Option>
			);
		}
		return options;
	}, [onAssign, onDelete]);

	return (
		<Groups>
			{Array.from(groups.entries()).map(([status, groupTickets]) => {
				const collapsed = isCollapsed(status);
				const contentId = `group-content-${status}`;
				return (
					<Group key={status} data-testid={`group-${status}`}>
						<TicketGroupHeader
							status={status}
							count={groupTickets.length}
							collapsed={collapsed}
							contentId={contentId}
							onToggle={() => toggle(status)}
						/>

						<div id={contentId} data-testid={contentId} hidden={collapsed}>
							<TableWrap>
								<Table
									// Remounting is the only way to clear the table's own
									// selection state, so the epoch is part of the key.
									key={`${status}-${selectionEpoch}`}
									data={ticketTableRows(groupTickets)}
									getRowId={(row: TicketTableRow) => row.rowId}
									width="100%"
									data-cy={`ticket-table-${status}`}
									onSelectionChange={
										onSelectionChange
											? (rows: TicketTableRow[]) =>
													onSelectionChange(
														status,
														rows.map(row => row.ticket)
													)
											: undefined
									}
								>
									<Table.TextColumn
										accessorKey="slug"
										header={COLUMN_LABELS.id}
										width={COLUMN_WIDTHS.id}
										data-cy="ticket-id"
									/>
									<Table.TextColumn
										accessorKey="subject"
										header={COLUMN_LABELS.subject}
										ellipsis
										data-cy="ticket-subject"
										onClick={(row: TicketTableRow) => () => navigate(`/tickets/${row.slug}`)}
									/>
									<Table.AvatarColumn
										accessorKey="requesterName"
										header={COLUMN_LABELS.requester}
										width={COLUMN_WIDTHS.requester}
										visible={visible.has('requester')}
										// Requesters are contacts, not Forecast people, so there
										// is no profile picture to load — the avatar falls back
										// to initials.
										image={() => undefined}
										ellipsis
									/>
									<Table.AvatarColumn
										accessorKey="assigneeName"
										header={COLUMN_LABELS.assignee}
										width={COLUMN_WIDTHS.assignee}
										visible={visible.has('assignee')}
										image={() => undefined}
										ellipsis
									/>
									<Table.TextColumn
										accessorKey="priorityLabel"
										header={COLUMN_LABELS.priority}
										width={COLUMN_WIDTHS.priority}
										visible={visible.has('priority')}
										icon={(row: TicketTableRow) => <PriorityFlag priority={row.ticket.priority} />}
									/>
									<Table.TextColumn
										accessorKey="created"
										header={COLUMN_LABELS.created}
										width={COLUMN_WIDTHS.created}
										visible={visible.has('created')}
									/>
									<Table.TextColumn
										accessorKey="labelsText"
										header={COLUMN_LABELS.labels}
										width={COLUMN_WIDTHS.labels}
										visible={visible.has('labels')}
										ellipsis
									/>
									<Table.TextColumn
										accessorKey="projectLabel"
										header={COLUMN_LABELS.project}
										width={COLUMN_WIDTHS.project}
										visible={visible.has('project')}
										ellipsis
									/>
									<Table.TextColumn
										accessorKey="slaText"
										header={COLUMN_LABELS.sla}
										width={COLUMN_WIDTHS.sla}
										visible={visible.has('sla')}
										icon={(row: TicketTableRow) => {
											const {state, icon, iconColor} = slaPresentation(row.ticket.sla);
											// The glyph carries the SLA state; wrapping it in our
											// own element is what makes the state assertable, since
											// the design-system Icon renders no identifying attribute.
											return (
												<span data-testid="sla-icon" data-sla-state={state}>
													<Icon icon={icon} size="s" newColor={iconColor} />
												</span>
											);
										}}
									/>
									{/* The action column's options must be a hole-free list: the
									    design system maps over them and reads `props.hidden` off
									    each one, so a conditional `null` in the list throws. */}
									{actionOptions.length > 0 ? (
										<Table.ActionColumn header="Ticket actions" hideHeader>
											{actionOptions}
										</Table.ActionColumn>
									) : null}
								</Table>
							</TableWrap>

							<MobileStack data-testid={`mobile-stack-${status}`}>
								{groupTickets.map(ticket => (
									<TicketListCard key={ticket.id} ticket={ticket} />
								))}
							</MobileStack>
						</div>
					</Group>
				);
			})}
		</Groups>
	);
}
